import type { SiteAlertType } from "@repo/api-dtos"
import type { AdminSiteAlert } from "@repo/marketplace-admin"
import { createFileRoute } from "@tanstack/react-router"
import {
	AlertCircle,
	CheckCircle2,
	Info,
	TriangleAlert,
	XCircle,
} from "lucide-react"
import { useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"
import { toast } from "sonner"
import { PageHeader, Section, SectionStack } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot, type StatusTone } from "@/components/ui/status"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { SettingRow, SettingRows } from "@/features/integration-apps/SettingRow"
import { useSiteAlert, useUpsertSiteAlert } from "@/queries/marketplace"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/site-alert")({
	component: SiteAlertPage,
})

const ALERT_TYPES: Array<{
	value: SiteAlertType
	label: string
	icon: React.ComponentType<{ className?: string }>
}> = [
	{ value: "info", label: "Info", icon: Info },
	{ value: "warning", label: "Warning", icon: TriangleAlert },
	{ value: "error", label: "Error", icon: XCircle },
	{ value: "success", label: "Success", icon: CheckCircle2 },
]

const previewMarkdownComponents = {
	a: ({ href, children }: React.ComponentPropsWithoutRef<"a">) => (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="font-semibold underline underline-offset-2"
		>
			{children}
		</a>
	),
	p: ({ children }: React.ComponentPropsWithoutRef<"p">) => (
		<p className="mb-1 last:mb-0">{children}</p>
	),
}

const PREVIEW_CLASSES: Record<SiteAlertType, string> = {
	info: "bg-blue-100 text-blue-950 dark:bg-blue-950 dark:text-blue-100",
	warning: "bg-amber-100 text-amber-950 dark:bg-amber-950 dark:text-amber-100",
	error: "bg-red-100 text-red-950 dark:bg-red-950 dark:text-red-100",
	success:
		"bg-emerald-100 text-emerald-950 dark:bg-emerald-950 dark:text-emerald-100",
}

interface Draft {
	enabled: boolean
	type: SiteAlertType
	loggedInOnly: boolean
	startsAt: string
	endsAt: string
	messageEn: string
	messageFr: string
}

function isoToLocalInput(iso: string | null): string {
	if (!iso) return ""
	const d = new Date(iso)
	const pad = (n: number) => String(n).padStart(2, "0")
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function draftFromAlert(alert: AdminSiteAlert | null): Draft {
	if (!alert) {
		return {
			enabled: false,
			type: "info",
			loggedInOnly: true,
			startsAt: isoToLocalInput(new Date().toISOString()),
			endsAt: "",
			messageEn: "",
			messageFr: "",
		}
	}

	return {
		enabled: alert.enabled,
		type: alert.type,
		loggedInOnly: alert.loggedInOnly,
		startsAt: isoToLocalInput(alert.startsAt),
		endsAt: isoToLocalInput(alert.endsAt),
		messageEn: alert.messages.en,
		messageFr: alert.messages.fr ?? "",
	}
}

type AlertStatus = "live" | "scheduled" | "expired" | "off"

function alertStatus(alert: AdminSiteAlert | null): AlertStatus {
	if (!alert || !alert.enabled) return "off"
	const now = new Date()
	if (new Date(alert.startsAt) > now) return "scheduled"
	if (alert.endsAt && new Date(alert.endsAt) <= now) return "expired"
	return "live"
}

const STATUS_LABELS: Record<AlertStatus, { label: string; tone: StatusTone }> =
	{
		live: { label: "Live, visible to everyone", tone: "ok" },
		scheduled: {
			label: "Scheduled, will show at the start date",
			tone: "info",
		},
		expired: { label: "Expired, past its end date", tone: "neutral" },
		off: { label: "Off, not shown", tone: "neutral" },
	}

function SiteAlertPage() {
	const { alert, isLoading, error } = useSiteAlert()
	const upsert = useUpsertSiteAlert()

	const [draft, setDraft] = useState<Draft>(() => draftFromAlert(null))
	const [dirty, setDirty] = useState(false)

	useEffect(() => {
		if (!isLoading && !dirty) {
			setDraft(draftFromAlert(alert))
		}
	}, [alert, isLoading, dirty])

	function updateDraft(patch: Partial<Draft>) {
		setDirty(true)
		setDraft((d) => ({ ...d, ...patch }))
	}

	function save() {
		if (!draft.messageEn.trim()) {
			toast.error("The English message is required")
			return
		}
		if (!draft.startsAt) {
			toast.error("A start date is required")
			return
		}
		const startsAt = new Date(draft.startsAt)
		const endsAt = draft.endsAt ? new Date(draft.endsAt) : null
		if (endsAt && endsAt <= startsAt) {
			toast.error("The end date must be after the start date")
			return
		}

		upsert.mutate(
			{
				enabled: draft.enabled,
				type: draft.type,
				loggedInOnly: draft.loggedInOnly,
				startsAt: startsAt.toISOString(),
				endsAt: endsAt ? endsAt.toISOString() : null,
				messages: {
					en: draft.messageEn.trim(),
					fr: draft.messageFr.trim() || undefined,
				},
			},
			{ onSuccess: () => setDirty(false) },
		)
	}

	const status = alertStatus(alert)
	const statusLabel = STATUS_LABELS[status]
	const previewType = draft.type
	const PreviewIcon =
		ALERT_TYPES.find((t) => t.value === previewType)?.icon ?? Info

	return (
		<div className="flex max-w-4xl flex-col gap-6 p-6">
			<PageHeader
				title="Site alert"
				description="One site-wide banner shown at the top of every CardNexus page, like the maintenance banner. Visitors can dismiss it for the day; it comes back if you edit it."
				actions={
					!isLoading && (
						<StatusDot tone={statusLabel.tone}>{statusLabel.label}</StatusDot>
					)
				}
			/>

			{error ? (
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					Error: {error.message}
				</div>
			) : isLoading ? (
				<div className="flex flex-col gap-4">
					<Skeleton className="h-8 w-[200px]" />
					<Skeleton className="h-64 w-full" />
				</div>
			) : (
				<SectionStack>
					<Section label="Display">
						<SettingRows>
							<SettingRow
								label="Show the alert"
								htmlFor="alert-enabled"
								help="When on, the banner shows everywhere within the scheduled window."
							>
								<Switch
									id="alert-enabled"
									checked={draft.enabled}
									onCheckedChange={(enabled) => updateDraft({ enabled })}
								/>
							</SettingRow>
							<SettingRow
								label="Only show to logged-in users"
								htmlFor="alert-logged-in-only"
								help="When checked, anonymous visitors never see the banner."
							>
								<Checkbox
									id="alert-logged-in-only"
									checked={draft.loggedInOnly}
									onCheckedChange={(checked) =>
										updateDraft({ loggedInOnly: checked === true })
									}
								/>
							</SettingRow>
							<SettingRow label="Type" htmlFor="alert-type">
								<Select
									value={draft.type}
									onValueChange={(type) =>
										updateDraft({ type: type as SiteAlertType })
									}
								>
									<SelectTrigger id="alert-type" className="w-full">
										<SelectValue placeholder="Type" />
									</SelectTrigger>
									<SelectContent>
										{ALERT_TYPES.map((t) => (
											<SelectItem key={t.value} value={t.value}>
												<span className="flex items-center gap-2">
													<t.icon className="h-4 w-4" />
													{t.label}
												</span>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</SettingRow>
						</SettingRows>
					</Section>

					<Section label="Schedule">
						<SettingRows>
							<SettingRow label="Start date" htmlFor="alert-starts">
								<Input
									id="alert-starts"
									type="datetime-local"
									value={draft.startsAt}
									onChange={(e) => updateDraft({ startsAt: e.target.value })}
								/>
							</SettingRow>
							<SettingRow
								label="End date (optional)"
								htmlFor="alert-ends"
								help="Leave empty to keep the alert up until you turn it off."
							>
								<Input
									id="alert-ends"
									type="datetime-local"
									value={draft.endsAt}
									onChange={(e) => updateDraft({ endsAt: e.target.value })}
								/>
							</SettingRow>
						</SettingRows>
					</Section>

					<Section label="Message">
						<SettingRows>
							<SettingRow
								label="English"
								htmlFor="alert-message-en"
								help={
									<>
										Required: shown to every visitor without a French
										translation. Markdown is supported, for example{" "}
										<code className="font-mono">
											[link text](https://example.com)
										</code>{" "}
										and <code className="font-mono">**bold**</code>.
									</>
								}
								wide
							>
								<Textarea
									id="alert-message-en"
									rows={3}
									placeholder="e.g. Payouts are delayed by our payment provider, no action needed on your side."
									value={draft.messageEn}
									onChange={(e) => updateDraft({ messageEn: e.target.value })}
								/>
							</SettingRow>
							<SettingRow
								label="French"
								htmlFor="alert-message-fr"
								help="Optional: French visitors see the English message when empty."
								wide
							>
								<Textarea
									id="alert-message-fr"
									rows={3}
									placeholder="e.g. Les paiements sont retardés par notre prestataire, aucune action requise de votre côté."
									value={draft.messageFr}
									onChange={(e) => updateDraft({ messageFr: e.target.value })}
								/>
							</SettingRow>
						</SettingRows>
					</Section>

					<Section label="Preview">
						<div
							className={cn(
								"flex items-center justify-center gap-2 rounded-md px-4 py-3 font-medium",
								PREVIEW_CLASSES[previewType],
							)}
						>
							<PreviewIcon className="h-4 w-4 shrink-0" />
							{draft.messageEn.trim() ? (
								<div>
									<ReactMarkdown components={previewMarkdownComponents}>
										{draft.messageEn}
									</ReactMarkdown>
								</div>
							) : (
								<span>Your message will appear here</span>
							)}
						</div>
					</Section>

					<div className="flex flex-col items-end gap-2">
						<div className="flex items-center gap-3">
							{alert && (
								<p className="text-xs text-meta">
									Last saved {new Date(alert.updatedAt).toLocaleString()}
								</p>
							)}
							<Button onClick={save} disabled={upsert.isPending}>
								{upsert.isPending ? "Saving…" : "Save alert"}
							</Button>
						</div>
						{status === "live" && draft.enabled === false && (
							<p className="flex items-center gap-2 text-warn text-xs">
								<AlertCircle className="h-3.5 w-3.5" />
								Turning the switch off only takes effect after saving.
							</p>
						)}
					</div>
				</SectionStack>
			)}
		</div>
	)
}
